import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import { uiAr, uiEn } from '@virtual-care/ui';

import ar from './locales/ar.ts';
import en from './locales/en.ts';

export const SUPPORTED_LANGUAGES = ['ar', 'en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: SupportedLanguage = 'ar';

export function isSupportedLanguage(value: string): value is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

export function directionFor(language: string): 'rtl' | 'ltr' {
  return language === 'ar' ? 'rtl' : 'ltr';
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ar: { translation: ar, ui: uiAr },
      en: { translation: en, ui: uiEn },
    },
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES,
    ns: ['translation', 'ui'],
    defaultNS: 'translation',
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },
    returnNull: false,
  });

const initial = isSupportedLanguage(i18n.language)
  ? i18n.language
  : DEFAULT_LANGUAGE;

if (typeof document !== 'undefined') {
  document.documentElement.lang = initial;
  document.documentElement.dir = directionFor(initial);
}

i18n.on('languageChanged', (lng) => {
  if (typeof document === 'undefined') return;
  const language = isSupportedLanguage(lng) ? lng : DEFAULT_LANGUAGE;
  document.documentElement.lang = language;
  document.documentElement.dir = directionFor(language);
});

export default i18n;
